// Line-count audit (2026-05-04): 600 lines.
import { useCallback } from 'react';
import { z } from 'zod';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useResponsiveStore } from '@/stores/editor/responsive-store';
import { propertyModificationService } from '@/services/ai/PropertyModificationService';
import { aiContextService } from '@/services/ai/AIContextService';
import { componentReferenceResolver } from '@/services/ai/ComponentReferenceResolver';
import {
  parseMessageMentions,
  resolveMentions,
  validateMentions,
  getMentionsByType,
  buildDataContext,
  buildPageContext,
  type ResolvedMessage,
  type DataContext,
  type PageContext,
} from '@/services/ai/mentions';
import { commandStateManager, type ComponentSnapshot } from '@/services/ai/CommandStateManager';
import { contextBuilder, type AIPropertyContext } from '@/services/ai/ContextBuilder';
import { buildSafeAiComponentContextFromComponent } from '@/services/ai/AIComponentContextSanitizer';
import type { AiBindingContextSummary } from '@/services/ai/BindingContextSummary';
import { templateInstantiationService } from '@/services/ai/TemplateInstantiationService';
import { getBlockedAICommandPermissionMessage } from '@/services/ai/agentCommandPermissions';
import { config } from '@/config';
import { filterDimensionChanges, isDimensionPropertyChange } from '@/utils/agentContextBuilder';
import { Component } from '@/types';
import type { ComponentData, LayoutData, PropertyMessage } from '@/components/ai/types';
import type {
  UseAgentPropertyFlowDeps,
  UseAgentPropertyFlowReturn,
} from './agentMessagingTypes';
import type { ParsedCanvasInsertionPlan } from './useCanvasTextParser';
import {
  useAgentPropertyCommandExecution,
  type PropertyFlowResponseData,
} from './useAgentPropertyCommandExecution';
import {
  copySanitizedOwnEnumerableProperties,
  mergeSanitizedRecords,
  omitSanitizedRecordKeys,
} from '@/utils/safeRecordSanitization';

function parsePropertyChangesRecord(
  responseText: string,
  target: Component
): Record<string, unknown> | undefined {
  // Intentional local exception: backend/agent response text is not a normal
  // runtime quick command, so it stays on the response parser fallback.
  const parsedChanges = propertyModificationService.parsePropertyChanges(responseText, target);
  const parseResult = z.record(z.string(), z.unknown()).safeParse(parsedChanges);
  return parseResult.success ? parseResult.data : undefined;
}

function copySafeOwnEnumerableProperties(
  target: Record<string, unknown>,
  source: Record<string, unknown> | undefined
): void {
  copySanitizedOwnEnumerableProperties(target, source);
}

function mergeSafeOwnEnumerableProperties(
  ...sources: Array<Record<string, unknown> | undefined>
): Record<string, unknown> {
  return mergeSanitizedRecords(...sources);
}

function omitSafeOwnEnumerableProperties(
  source: Record<string, unknown>,
  keys: Iterable<string>
): Record<string, unknown> {
  return omitSanitizedRecordKeys(source, keys);
}

function withTargetComponentId(
  propertyChanges: Record<string, unknown> | undefined,
  targetComponentId: string
): Record<string, unknown> | undefined {
  if (!propertyChanges) {
    return undefined;
  }

  return mergeSafeOwnEnumerableProperties(propertyChanges, { targetComponentId });
}

function parseUnknownRecord(value: unknown): Record<string, unknown> | undefined {
  const result = z.record(z.string(), z.unknown()).safeParse(value);
  return result.success ? result.data : undefined;
}

function describePropertyChange(key: string, value: unknown): string {
  const valueRecord = z.record(z.string(), z.unknown()).safeParse(value);
  const typedValue = valueRecord.success ? valueRecord.data : null;

  if (key === 'background' && typedValue?.color) {
    return `background color to ${typedValue.color}`;
  }
  if (key === 'grid' && typedValue?.enabled !== undefined) {
    return typedValue.enabled ? 'grid enabled' : 'grid disabled';
  }
  if (key === 'theme' && typedValue?.colorScheme) {
    return `${typedValue.colorScheme} mode`;
  }
  if (key === 'layout' && typedValue) {
    const items: string[] = [];
    if (typedValue.padding) {
      items.push('padding updated');
    }
    if (typedValue.border) {
      items.push('border applied');
    }
    if (typedValue.shadow) {
      items.push('shadow added');
    }
    return items.join(', ');
  }

  return `${key} updated`;
}

const MISSING_TEMPLATE_PAGE_ERROR = 'No active page. Please create or select a page before adding templates.';
const INVALID_CANVAS_INSERTION_PLAN_ERROR = 'Unable to apply canvas insertion: malformed plan.';

type CanvasInsertionPlanResult =
  | { status: 'absent' }
  | { status: 'invalid' }
  | { status: 'valid'; plan: ParsedCanvasInsertionPlan };

type CanvasInsertionExecutionResult = {
  placementSucceeded: boolean;
  finalContent?: string;
};

const canvasComponentDataSchema = z.object({
  type: z.string(),
  id: z.string().optional(),
  title: z.string().optional(),
  data: z.unknown().optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
  placement: z.object({
    position: z.object({
      x: z.number(),
      y: z.number(),
    }),
    size: z.object({
      width: z.number(),
      height: z.number(),
    }),
    reasoning: z.string(),
  }).optional(),
  metadata: z.object({
    generatedByAI: z.boolean(),
    componentType: z.string(),
    confidence: z.number(),
    canPlace: z.boolean(),
  }).optional(),
  created: z.boolean().optional(),
  error: z.string().optional(),
  details: z.array(z.string()).optional(),
  detectedType: z.string().optional(),
}).passthrough();

const canvasInsertionPlanSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('singleComponent'),
    component: canvasComponentDataSchema,
    message: z.string(),
  }),
  z.object({
    kind: z.literal('multipleComponents'),
    components: z.array(canvasComponentDataSchema).nonempty(),
    message: z.string(),
  }),
  z.object({
    kind: z.literal('template'),
    templateId: z.string(),
    templateName: z.string(),
    componentCount: z.number(),
    position: z.object({
      x: z.number(),
      y: z.number(),
    }),
    message: z.string(),
  }),
]);

function parseCanvasInsertionPlan(plan: unknown): ParsedCanvasInsertionPlan | null {
  const result = canvasInsertionPlanSchema.safeParse(plan);
  return result.success ? result.data : null;
}

function hasCanvasInsertionPlanKey(
  propertyChanges: Record<string, unknown> | undefined
): propertyChanges is Record<string, unknown> {
  return Boolean(propertyChanges && Object.hasOwn(propertyChanges, 'aiCanvasInsertion'));
}

function getCanvasInsertionPlan(
  propertyChanges: Record<string, unknown> | undefined
): CanvasInsertionPlanResult {
  if (!hasCanvasInsertionPlanKey(propertyChanges)) {
    return { status: 'absent' };
  }

  const plan = parseCanvasInsertionPlan(propertyChanges.aiCanvasInsertion);
  return plan ? { status: 'valid', plan } : { status: 'invalid' };
}

function removeCanvasInsertionPlan(
  propertyChanges: Record<string, unknown>
): Record<string, unknown> | undefined {
  const remainingChanges = omitSafeOwnEnumerableProperties(propertyChanges, ['aiCanvasInsertion']);
  return Object.keys(remainingChanges).length > 0 ? remainingChanges : undefined;
}

export interface AiPropertyFlowComponentPayload {
  id: string;
  type: Component["type"];
  name?: string;
  properties: Record<string, unknown>;
  bindingContext?: AiBindingContextSummary;
  style: Component["style"];
  size: Component["size"];
  position: Component["position"];
  responsivePosition: Component["responsivePosition"];
}

export function buildAiPropertyFlowComponentPayload(
  component: Component,
  relatedComponents: Component[] = [],
): AiPropertyFlowComponentPayload {
  const safeComponentContext = buildSafeAiComponentContextFromComponent(
    component,
    relatedComponents,
  );

  return {
    id: component.id,
    type: component.type,
    name: component.name,
    properties: safeComponentContext.properties,
    bindingContext: safeComponentContext.bindingContext,
    style: component.style,
    size: component.size,
    position: component.position,
    responsivePosition: component.responsivePosition,
  };
}

export function useAgentPropertyFlow(
  deps: UseAgentPropertyFlowDeps
): UseAgentPropertyFlowReturn {
  const {
    inputValue,
    setInputValue,
    setIsLoading,
    setError,
    setMessages,
    component,
    multipleComponents,
    isCanvas,
    project,
    tokens,
    updateComponent,
    moveComponent,
    resizeComponent,
    canvasSettings,
    setPendingClarification,
    addToHistory,
    persistMessage,
    generateMessageId,
    currentMentionContextRef,
    parseCanvasChanges,
    applyPropertyChanges,
    handlePlaceComponent,
    handlePlaceComponents,
    commandPermissionGuard,
  } = deps;

  const { executeDirectCommand } = useAgentPropertyCommandExecution({
    component,
    multipleComponents,
    isCanvas,
    project,
    updateComponent,
    moveComponent,
    resizeComponent,
    setMessages,
    setInputValue,
    setIsLoading,
    setPendingClarification,
    addToHistory,
    persistMessage,
    generateMessageId,
    applyPropertyChanges,
    commandPermissionGuard,
  });

  const handlePropertyFlow = useCallback(
    async (
      userInput: string,
      effectiveComponent: Component | null | undefined
    ): Promise<void> => {
      let targetComponentFromReference: Component | null = null;
      const templateInstanceId = typeof component?.properties?._templateInstanceId === 'string'
        ? component.properties._templateInstanceId
        : undefined;

      if (isCanvas || (component && component.properties && templateInstanceId)) {
        const references = componentReferenceResolver.extractComponentReferences(userInput);
        if (references.length > 0) {
          const resolved = componentReferenceResolver.resolveReference(references[0], {
            selectedComponents: multipleComponents || (component ? [component] : []),
            templateInstanceId,
          });

          if (resolved) {
            targetComponentFromReference = resolved.component;
          }
        }
      }

      if (!isCanvas && !targetComponentFromReference) {
        const targetComponent = effectiveComponent || component || multipleComponents?.[0];
        if (!targetComponent) {
          return;
        }
      }

      let contextString = '';
      let enhancedContext: ReturnType<typeof aiContextService.collectEnhancedComponentContext> | undefined;

      try {
        const contextData = aiContextService.collectContext();
        contextString = aiContextService.formatContextForAI(contextData);
      } catch (error) {
        console.warn('🤖 Failed to collect context, proceeding without:', error);
      }

      const enhancedContextTarget =
        targetComponentFromReference || effectiveComponent || component || multipleComponents?.[0];
      if (enhancedContextTarget) {
        try {
          enhancedContext = aiContextService.collectEnhancedComponentContext(enhancedContextTarget, {
            includeParent: true,
            includeSiblings: true,
            includeViewport: true,
            includeLayering: true,
            maxSiblings: 5,
          });
        } catch (error) {
          console.warn('🤖 Failed to collect enhanced context:', error);
        }
      }

      const userMessage: PropertyMessage = {
        id: generateMessageId(),
        content: userInput,
        role: 'user',
        timestamp: new Date(),
        context: contextString || undefined,
      };

      setMessages((previous) => [...previous, userMessage]);
      await persistMessage(userMessage);
      setInputValue('');
      setIsLoading(true);
      setError(null);

      const assistantMessageId = generateMessageId();
      let assistantContent = '';

      const applyResolvedPropertyChanges = (
        propertyChanges: Record<string, unknown> | undefined,
        stateSnapshot: ComponentSnapshot | null
      ): boolean => {
        if (!propertyChanges || Object.keys(propertyChanges).length === 0) {
          return false;
        }

        if (isDimensionPropertyChange(propertyChanges)) {
          const targetComp = effectiveComponent || multipleComponents?.[0] || null;
          const filteredChanges = filterDimensionChanges(
            userInput,
            propertyChanges,
            targetComp,
            stateSnapshot
          );
          applyPropertyChanges(assistantMessageId, filteredChanges);
          return true;
        }

        applyPropertyChanges(assistantMessageId, propertyChanges);
        return true;
      };

      const executeCanvasInsertionPlan = async (
        plan: ParsedCanvasInsertionPlan,
        messageId: string
      ): Promise<CanvasInsertionExecutionResult> => {
        const selectedComponent = component || multipleComponents?.[0];
        const insertionPermission = commandPermissionGuard?.({
          categories: ['insertion'],
          project,
          targetComponents: selectedComponent ? [selectedComponent] : [],
        });

        if (insertionPermission && !insertionPermission.allowed) {
          const blockedContent = getBlockedAICommandPermissionMessage(insertionPermission, 'insertion');
          setMessages((previous) => previous.map((message) =>
            message.id === messageId
              ? { ...message, content: blockedContent, isStreaming: false, applied: false }
              : message
          ));
          return { placementSucceeded: false, finalContent: blockedContent };
        }

        let placementSucceeded = false;
        if (plan.kind === 'singleComponent') {
          placementSucceeded = await handlePlaceComponent(plan.component, {
            suppressAssistantMessage: true,
          });
        } else if (plan.kind === 'multipleComponents') {
          placementSucceeded = await handlePlaceComponents(plan.components, plan.message, {
            suppressAssistantMessage: true,
          });
        } else {
          if (!project?.id) {
            setError('No active project. Please create or open a project first.');
            return { placementSucceeded: false };
          }

          const targetPageId = project.currentPageId;
          if (!targetPageId) {
            setError(MISSING_TEMPLATE_PAGE_ERROR);
            return { placementSucceeded: false };
          }

          const instance = await templateInstantiationService.instantiateTemplate(
            plan.templateId,
            project.id,
            targetPageId,
            plan.position
          );

          if (!instance) {
            setError(`Failed to create ${plan.templateName} template.`);
            return { placementSucceeded: false };
          }

          placementSucceeded = await handlePlaceComponents(instance.components, plan.message, {
            suppressAssistantMessage: true,
          });
        }

        if (placementSucceeded) {
          setMessages((previous) => previous.map((message) =>
            message.id === messageId ? { ...message, applied: true } : message
          ));
        }

        return { placementSucceeded };
      };

      try {
        setMessages((previous) => [...previous, {
          id: assistantMessageId,
          content: '',
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: true,
        }]);

        const storeState = useEditorStore.getState();
        const freshComponent = component
          ? storeState.components.find((current) => current.id === component.id) || component
          : null;
        const freshMultipleComponents = multipleComponents?.map((current) =>
          storeState.components.find((storeComponent) => storeComponent.id === current.id) || current
        );

        let stateSnapshot: ComponentSnapshot | null = null;
        let phase51Context: AIPropertyContext | null = null;

        if (freshComponent && !isCanvas) {
          stateSnapshot = commandStateManager.captureState(freshComponent);

          const { activeViewport } = useResponsiveStore.getState();
          phase51Context = contextBuilder.buildFromSnapshot(stateSnapshot, {
            currentBreakpoint: activeViewport || 'desktop',
            viewportWidth: 1200,
            viewportHeight: 800,
            enhancedContext,
          });

          contextBuilder.validate(phase51Context);
        }

        let resolvedMessage: ResolvedMessage | null = null;
        let dataContext: DataContext | null = null;
        let pageContext: PageContext | null = null;

        if (/(?<!\\)[@#]/.test(inputValue)) {
          const parsedMessage = parseMessageMentions(inputValue);
          resolvedMessage = await resolveMentions(parsedMessage);
          validateMentions(resolvedMessage);

          const dataMentions = getMentionsByType(resolvedMessage, 'data');
          if (dataMentions.filter((mention) => mention !== null).length > 0) {
            dataContext = await buildDataContext(resolvedMessage.resolvedMentions);
          }

          const pageMentions = getMentionsByType(resolvedMessage, 'page');
          if (pageMentions.filter((mention) => mention !== null).length > 0) {
            pageContext = buildPageContext(resolvedMessage.resolvedMentions);
          }

          currentMentionContextRef.current = { resolvedMessage, pageContext };
        }

        const messageText = resolvedMessage?.cleanText || inputValue.trim();
        const requestBody = {
          message: messageText,
          context: isCanvas
            ? {
                component: {
                  type: 'canvas',
                  name: 'Canvas Settings',
                  properties: {
                    background: canvasSettings.background,
                    grid: canvasSettings.grid,
                    theme: canvasSettings.theme,
                  },
                  style: { padding: canvasSettings.layout?.padding },
                  size: { width: 1920, height: 1080 },
                  position: { x: 0, y: 0 },
                },
                dataContext: dataContext || undefined,
                pageContext: pageContext || undefined,
              }
            : {
                component: freshComponent
                  ? buildAiPropertyFlowComponentPayload(
                      freshComponent,
                      freshMultipleComponents ?? storeState.components,
                    )
                  : null,
                multipleComponents: freshMultipleComponents?.map((current) =>
                  buildAiPropertyFlowComponentPayload(
                    current,
                    freshMultipleComponents,
                  ),
                ),
                enhancedContext: enhancedContext || undefined,
                currentDimensions: phase51Context?.currentDimensions || undefined,
                dataContext: dataContext || undefined,
                pageContext: pageContext || undefined,
              },
          isCanvas,
          options: {
            provider: 'redpill',
            model: 'phala/gemma-3-27b-it',
            temperature: 0.3,
            maxTokens: 1024,
          },
        };

        const directCommandResult = await executeDirectCommand({
          userInput,
          assistantMessageId,
          targetComponentFromReference,
          effectiveComponent,
        });

        if (directCommandResult.handled) {
          return;
        }

        const skipAssistantPropertyParsing = directCommandResult.skipAssistantPropertyParsing;
        const shouldUseBackend = directCommandResult.shouldUseBackend;
        let data: PropertyFlowResponseData = directCommandResult.data || {};

        if (shouldUseBackend) {
          const response = await fetch(`${config.api.baseUrl}/ai/property-modification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokens?.accessToken}`,
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`AI error: ${response.status} - ${errorData.error || response.statusText}`);
          }

          data = await response.json();
        }

        if (data.success && data.content) {
          assistantContent = data.content;
          const componentData: ComponentData | undefined = data.componentData;
          const layoutData: LayoutData | undefined = data.layoutData;

          let propertyChanges: Record<string, unknown> | undefined;
          if (data.propertyChanges) {
            propertyChanges = mergeSafeOwnEnumerableProperties(data.propertyChanges);
          } else {
            const jsonMatch = data.content.match(/```json\s*([\s\S]*?)```/);
            if (jsonMatch) {
              try {
                const jsonData: unknown = JSON.parse(jsonMatch[1]);
                const jsonResult = z.record(z.string(), z.unknown()).safeParse(jsonData);
                if (!jsonResult.success) {
                  throw new Error('Expected JSON object from AI response');
                }

                const jsonObject = jsonResult.data;
                propertyChanges = {};

                copySafeOwnEnumerableProperties(propertyChanges, parseUnknownRecord(jsonObject.style));
                copySafeOwnEnumerableProperties(propertyChanges, parseUnknownRecord(jsonObject.properties));
                if (jsonObject.size && typeof jsonObject.size === 'object' && jsonObject.size !== null) {
                  const sizeResult = z.record(z.string(), z.unknown()).safeParse(jsonObject.size);
                  if (sizeResult.success) {
                    if (sizeResult.data.width) {
                      propertyChanges.width = sizeResult.data.width;
                    }
                    if (sizeResult.data.height) {
                      propertyChanges.height = sizeResult.data.height;
                    }
                  }
                }
              } catch (error) {
                if (isCanvas) {
                  propertyChanges = parseCanvasChanges(data.content);
                  if (targetComponentFromReference) {
                    propertyChanges = withTargetComponentId(
                      propertyChanges,
                      targetComponentFromReference.id
                    );
                  }
                } else {
                  const parsedTarget =
                    targetComponentFromReference || effectiveComponent || component || multipleComponents?.[0];
                  if (parsedTarget) {
                    propertyChanges = parsePropertyChangesRecord(data.content, parsedTarget);
                  }
                }
                console.warn('Failed to parse JSON from AI response:', error);
              }
            } else if (isCanvas) {
              propertyChanges = parseCanvasChanges(data.content);
              if (targetComponentFromReference) {
                propertyChanges = withTargetComponentId(
                  propertyChanges,
                  targetComponentFromReference.id
                );
              }
            } else {
              const parsedTarget =
                targetComponentFromReference || effectiveComponent || component || multipleComponents?.[0];
              if (parsedTarget) {
                propertyChanges = parsePropertyChangesRecord(data.content, parsedTarget);
              }
            }
          }

          const canvasInsertionPlan = getCanvasInsertionPlan(propertyChanges);
          if (canvasInsertionPlan.status === 'invalid' && propertyChanges) {
            propertyChanges = removeCanvasInsertionPlan(propertyChanges);
          }

          const assistantMessage: PropertyMessage = {
            id: assistantMessageId,
            content: assistantContent,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
            componentData,
            layoutData,
            propertyChanges,
            applied: false,
          };

          setMessages((previous) => previous.map((message) =>
            message.id === assistantMessageId
              ? assistantMessage
              : message
          ));
          let finalAssistantMessage = assistantMessage;

          if (canvasInsertionPlan.status === 'valid') {
            const insertionResult = await executeCanvasInsertionPlan(
              canvasInsertionPlan.plan,
              assistantMessageId
            );
            finalAssistantMessage = {
              ...assistantMessage,
              content: insertionResult.finalContent ?? assistantMessage.content,
              applied: insertionResult.placementSucceeded,
            };
            setMessages((previous) => previous.map((message) =>
              message.id === assistantMessageId ? finalAssistantMessage : message
            ));
          } else if (canvasInsertionPlan.status === 'invalid' && !propertyChanges) {
            setError(INVALID_CANVAS_INSERTION_PLAN_ERROR);
          } else {
            applyResolvedPropertyChanges(propertyChanges, stateSnapshot);
          }

          if (canvasInsertionPlan.status === 'absent' && data.isComponentCreation) {
            if (data.isMultiComponentCreation && data.components && Array.isArray(data.components)) {
              const placementSucceeded = await handlePlaceComponents(data.components, undefined, {
                suppressAssistantMessage: true,
              });

              if (placementSucceeded) {
                finalAssistantMessage = {
                  ...finalAssistantMessage,
                  applied: true,
                };
                setMessages((previous) => previous.map((message) =>
                  message.id === assistantMessageId
                    ? finalAssistantMessage
                    : message
                ));
              }
            } else if (componentData) {
              const placementSucceeded = await handlePlaceComponent(componentData, {
                suppressAssistantMessage: true,
              });
              if (placementSucceeded) {
                finalAssistantMessage = {
                  ...finalAssistantMessage,
                  applied: true,
                };
                setMessages((previous) => previous.map((message) =>
                  message.id === assistantMessageId
                    ? finalAssistantMessage
                    : message
                ));
              }
            }
          }
          await persistMessage(finalAssistantMessage);
        } else {
          assistantContent = data.content || "I'll help you with that.";

          let propertyChanges: Record<string, unknown> | undefined;
          if (!skipAssistantPropertyParsing) {
            if (isCanvas) {
              propertyChanges = parseCanvasChanges(assistantContent);
            } else {
              const parsedTarget = effectiveComponent || multipleComponents?.[0];
              if (parsedTarget) {
                propertyChanges = parsePropertyChangesRecord(assistantContent, parsedTarget);
              }
            }
          }

          const canvasInsertionPlan = getCanvasInsertionPlan(propertyChanges);
          if (canvasInsertionPlan.status === 'invalid' && propertyChanges) {
            propertyChanges = removeCanvasInsertionPlan(propertyChanges);
          }

          const assistantMessage: PropertyMessage = {
            id: assistantMessageId,
            content: assistantContent,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
            propertyChanges,
            applied: false,
          };

          setMessages((previous) => previous.map((message) =>
            message.id === assistantMessageId
              ? assistantMessage
              : message
          ));
          let finalAssistantMessage = assistantMessage;

          if (canvasInsertionPlan.status === 'valid') {
            const insertionResult = await executeCanvasInsertionPlan(
              canvasInsertionPlan.plan,
              assistantMessageId
            );
            finalAssistantMessage = {
              ...assistantMessage,
              content: insertionResult.finalContent ?? assistantMessage.content,
              applied: insertionResult.placementSucceeded,
            };
            setMessages((previous) => previous.map((message) =>
              message.id === assistantMessageId ? finalAssistantMessage : message
            ));
          } else if (canvasInsertionPlan.status === 'invalid' && !propertyChanges) {
            setError(INVALID_CANVAS_INSERTION_PLAN_ERROR);
          } else {
            applyResolvedPropertyChanges(propertyChanges, null);
          }
          await persistMessage(finalAssistantMessage);
        }
      } catch (error) {
        console.error('Failed to send message:', error);

        let propertyChanges: Record<string, unknown> | undefined;
        if (isCanvas) {
          propertyChanges = parseCanvasChanges(userInput);
        } else {
          const parsedTarget = effectiveComponent || multipleComponents?.[0];
          if (parsedTarget) {
            propertyChanges = parsePropertyChangesRecord(userInput, parsedTarget);
          }
        }

        if (propertyChanges && Object.keys(propertyChanges).length > 0) {
          const canvasInsertionPlan = getCanvasInsertionPlan(propertyChanges);
          if (canvasInsertionPlan.status === 'invalid') {
            propertyChanges = removeCanvasInsertionPlan(propertyChanges);
          }

          const appliedMessage = canvasInsertionPlan.status === 'valid'
            ? canvasInsertionPlan.plan.message
            : propertyChanges
              ? `✅ Applied: ${Object.entries(propertyChanges)
                .map(([key, value]) => describePropertyChange(key, value))
                .join(', ')}`
              : INVALID_CANVAS_INSERTION_PLAN_ERROR;
          setMessages((previous) => previous.map((message) =>
            message.id === assistantMessageId
              ? {
                  ...message,
                  content: appliedMessage,
                  isStreaming: false,
                  propertyChanges,
                  applied: false,
                }
              : message
          ));

          let finalAssistantMessage: PropertyMessage = {
            id: assistantMessageId,
            content: appliedMessage,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
            propertyChanges,
            applied: false,
          };

          let applicationSucceeded = false;
          if (canvasInsertionPlan.status === 'valid') {
            const insertionResult = await executeCanvasInsertionPlan(
              canvasInsertionPlan.plan,
              assistantMessageId
            );
            applicationSucceeded = insertionResult.placementSucceeded;
            finalAssistantMessage = {
              ...finalAssistantMessage,
              content: insertionResult.finalContent ?? finalAssistantMessage.content,
              applied: insertionResult.placementSucceeded,
            };
            setMessages((previous) => previous.map((message) =>
              message.id === assistantMessageId ? finalAssistantMessage : message
            ));
          } else if (canvasInsertionPlan.status === 'invalid' && !propertyChanges) {
            setError(INVALID_CANVAS_INSERTION_PLAN_ERROR);
          } else {
            applicationSucceeded = applyResolvedPropertyChanges(propertyChanges, null);
          }

          await persistMessage(finalAssistantMessage);

          if (applicationSucceeded) {
            setError(null);
          }
        } else {
          setMessages((previous) => previous.filter((message) => message.id !== assistantMessageId));
          setError("AI service is temporarily unavailable. Try: 'add a chart', 'create a button', 'insert a table', 'twitter blue', 'minimal theme', 'dark mode', 'add a pie chart at the top'");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      inputValue,
      setInputValue,
      setIsLoading,
      setError,
      setMessages,
      component,
      multipleComponents,
      isCanvas,
      project,
      tokens,
      canvasSettings,
      persistMessage,
      generateMessageId,
      currentMentionContextRef,
      parseCanvasChanges,
      applyPropertyChanges,
      handlePlaceComponent,
      handlePlaceComponents,
      commandPermissionGuard,
      executeDirectCommand,
    ]
  );

  return { handlePropertyFlow };
}
